import { Injectable } from '@angular/core';
import { FeatureFlagsService, FeatureFlagVariation } from './feature-flags.service';

export interface ModalSeriesEntry {
  serieId: string;
  sessionId: string;
  type: string;
  milestone?: number;
  counter?: number;
  nbErrors?: number;
}

export interface UserLifeCycle {
  isUserRegistered: boolean;
  isUserEngaged: boolean;
  isUserGuest: boolean;
}

export interface AdDisplayContext {
  serieId: string;
  sessionId: string;
  serieType: string;
  totalQuestions: number;
  currentAnswerCount: number;
  questionIndex: number;
  goodAnswers: number;
  userLifeCycle: UserLifeCycle;
  isPackPermis: boolean;
  modalSeries: ModalSeriesEntry[];
}

export type AdDisplayDecision =
  | { shouldShow: false; updatedModalSeries: ModalSeriesEntry[] }
  | { shouldShow: true; goodAnswersForAd: number; updatedModalSeries: ModalSeriesEntry[] };

/**
 * Service responsible for determining when to display ads during series.
 *
 * This service fully encapsulates all ad display logic:
 * - Feature flag detection (multipleAdsPerSeries)
 * - Milestone-based behavior (new) vs random/last-question behavior (original)
 * - Special "erreurs" series behavior (every 15 questions)
 * - User eligibility checks
 * - Duplicate display prevention
 * - Tracking state management (returns updated modalSeries)
 *
 * The public API is minimal: just `shouldShowAd()` which handles all scenarios.
 * The caller only needs to persist the returned `updatedModalSeries` and display
 * the ad if `shouldShow` is true.
 */
@Injectable({ providedIn: 'root' })
export class SerieAdsService {
  private readonly DEFAULT_MILESTONES = [10, 20, 30];
  private readonly FEATURE_FLAG_NAME = 'multiple_ads_per_series';
  private readonly ERREURS_AD_INTERVAL = 15;

  constructor(private featureFlagsService: FeatureFlagsService) {}

  /**
   * Determines if an ad should be shown based on the current context.
   *
   * This is the main public API. It handles all series types and behaviors:
   * - entrainements/thematiques: milestone-based (with feature flag) or random/last-question
   * - erreurs: every 15 questions for engaged users without Pack Permis
   *
   * @param context - All information needed to make the decision
   * @returns Decision object with:
   *   - shouldShow: whether to display an ad
   *   - goodAnswersForAd: number of good answers to show in the ad (only when shouldShow is true)
   *   - updatedModalSeries: the updated tracking array to persist
   */
  shouldShowAd(context: AdDisplayContext): AdDisplayDecision {
    const { serieType } = context;

    // Handle erreurs series separately (different behavior)
    if (serieType === 'erreurs') {
      return this.evaluateErreursBehavior(context);
    }

    // Only entrainements and thematiques series show ads (besides erreurs)
    if (serieType !== 'entrainements' && serieType !== 'thematiques') {
      return { shouldShow: false, updatedModalSeries: context.modalSeries };
    }

    // Check if user is eligible for ads
    if (!this.isEligibleForEntrainementsAds(context.userLifeCycle, serieType, context.isPackPermis)) {
      return { shouldShow: false, updatedModalSeries: context.modalSeries };
    }

    // Choose behavior based on feature flag
    if (this.isMultipleAdsEnabled(serieType)) {
      return this.evaluateMilestoneBehavior(context);
    } else {
      return this.evaluateOriginalBehavior(context);
    }
  }

  // ============================================
  // Private implementation details below
  // ============================================

  private getMultipleAdsVariation(): FeatureFlagVariation | null {
    return this.featureFlagsService.getFeatureFlagVariation(this.FEATURE_FLAG_NAME);
  }

  private isMultipleAdsEnabled(serieType: string): boolean {
    const variation = this.getMultipleAdsVariation();
    return variation !== null && (serieType === 'entrainements' || serieType === 'thematiques');
  }

  private isEligibleForEntrainementsAds(
    userLifeCycle: UserLifeCycle,
    serieType: string,
    isPackPermis: boolean
  ): boolean {
    // Registered users on entrainements/thematiques always see ads
    if (userLifeCycle.isUserRegistered && (serieType === 'entrainements' || serieType === 'thematiques')) {
      return true;
    }

    // Engaged users on entrainements/thematiques see ads only if they don't have Pack Permis
    if (
      userLifeCycle.isUserEngaged &&
      (serieType === 'entrainements' || serieType === 'thematiques') &&
      !isPackPermis
    ) {
      return true;
    }

    return false;
  }

  private calculateAdMilestones(totalQuestions: number): number[] {
    const variation = this.getMultipleAdsVariation();
    const adsCount = variation?.data?.['ads_count'];

    if (typeof adsCount !== 'number' || adsCount <= 0) {
      return this.DEFAULT_MILESTONES.filter((m) => m <= totalQuestions);
    }

    // If adsCount >= totalQuestions, fall back to defaults
    if (adsCount >= totalQuestions) {
      return this.DEFAULT_MILESTONES.filter((m) => m <= totalQuestions);
    }

    // Calculate evenly distributed milestones across the entire series
    // Divide into (adsCount + 1) segments and place ads at segment boundaries
    // Using floating-point interval to ensure even distribution
    const interval = totalQuestions / (adsCount + 1);

    const milestones: number[] = [];
    for (let i = 1; i <= adsCount; i++) {
      const milestone = Math.round(interval * i);
      if (milestone > 0 && milestone <= totalQuestions) {
        milestones.push(milestone);
      }
    }

    // Remove potential duplicates from rounding and ensure sorted order
    return Array.from(new Set(milestones)).sort((a, b) => a - b);
  }

  private generateRandomValue(max: number): number {
    return Math.floor(Math.random() * max) + 1;
  }

  private isMilestoneAlreadyShown(
    modalSeries: ModalSeriesEntry[],
    serieId: string,
    sessionId: string,
    serieType: string,
    milestone: number
  ): boolean {
    return modalSeries.some(
      (entry) =>
        entry.serieId === serieId &&
        entry.sessionId === sessionId &&
        entry.type === serieType &&
        entry.milestone === milestone
    );
  }

  private isAdAlreadyShownForSession(
    modalSeries: ModalSeriesEntry[],
    serieId: string,
    sessionId: string,
    serieType: string
  ): boolean {
    return modalSeries.some(
      (entry) =>
        entry.serieId === serieId &&
        entry.sessionId === sessionId &&
        entry.type === serieType &&
        entry.milestone === undefined &&
        entry.counter === undefined // Exclude erreurs tracking entries
    );
  }

  private evaluateMilestoneBehavior(context: AdDisplayContext): AdDisplayDecision {
    const { serieId, sessionId, serieType, totalQuestions, currentAnswerCount, goodAnswers, modalSeries } = context;

    // Calculate milestones for this series
    const milestones = this.calculateAdMilestones(totalQuestions);

    // Check if we're at a milestone
    if (!milestones.includes(currentAnswerCount)) {
      return { shouldShow: false, updatedModalSeries: modalSeries };
    }

    // Check if this milestone has already been shown
    if (this.isMilestoneAlreadyShown(modalSeries, serieId, sessionId, serieType, currentAnswerCount)) {
      return { shouldShow: false, updatedModalSeries: modalSeries };
    }

    // Must have at least one good answer
    if (goodAnswers <= 0) {
      return { shouldShow: false, updatedModalSeries: modalSeries };
    }

    // Add tracking entry
    const updatedModalSeries = [
      ...modalSeries,
      {
        serieId,
        sessionId,
        type: serieType,
        milestone: currentAnswerCount,
      },
    ];

    return {
      shouldShow: true,
      goodAnswersForAd: goodAnswers,
      updatedModalSeries,
    };
  }

  private evaluateOriginalBehavior(context: AdDisplayContext): AdDisplayDecision {
    const {
      serieId,
      sessionId,
      serieType,
      totalQuestions,
      currentAnswerCount,
      questionIndex,
      goodAnswers,
      modalSeries,
    } = context;

    // Check if ad was already shown for this session
    if (this.isAdAlreadyShownForSession(modalSeries, serieId, sessionId, serieType)) {
      return { shouldShow: false, updatedModalSeries: modalSeries };
    }

    // Generate random value internally
    const randomValue = this.generateRandomValue(totalQuestions);

    // Random match or last question
    const randomMatch = randomValue === questionIndex + 1;
    const isLastQuestion = currentAnswerCount === totalQuestions - 1;

    if ((randomMatch || isLastQuestion) && goodAnswers > 0) {
      // Add tracking entry (no milestone for original behavior)
      const updatedModalSeries = [
        ...modalSeries,
        {
          serieId,
          sessionId,
          type: serieType,
        },
      ];

      return {
        shouldShow: true,
        goodAnswersForAd: goodAnswers,
        updatedModalSeries,
      };
    }

    return { shouldShow: false, updatedModalSeries: modalSeries };
  }

  private evaluateErreursBehavior(context: AdDisplayContext): AdDisplayDecision {
    const { serieId, sessionId, serieType, totalQuestions, userLifeCycle, isPackPermis, modalSeries } = context;

    // Only engaged users without Pack Permis see ads on erreurs
    if (!userLifeCycle.isUserEngaged || isPackPermis) {
      return { shouldShow: false, updatedModalSeries: modalSeries };
    }

    // Find existing tracking entry for this session
    const existingEntryIndex = modalSeries.findIndex(
      (entry) => entry.serieId === serieId && entry.sessionId === sessionId && entry.type === serieType
    );

    if (existingEntryIndex === -1) {
      // First time: create new entry with counter=0
      const updatedModalSeries = [
        ...modalSeries,
        {
          serieId,
          sessionId,
          type: serieType,
          counter: 0,
          nbErrors: totalQuestions,
        },
      ];

      return { shouldShow: false, updatedModalSeries };
    }

    // Update existing entry
    const existingEntry = modalSeries[existingEntryIndex];
    const newCounter = (existingEntry.counter || 0) + 1;
    const updatedEntry = { ...existingEntry, counter: newCounter };

    // Check if we should show ad (every 15 questions)
    if (newCounter % this.ERREURS_AD_INTERVAL === 0) {
      const previousNbErrors = existingEntry.nbErrors || totalQuestions;
      const goodAnswersForAd = previousNbErrors - totalQuestions;

      // Reset nbErrors for next interval calculation
      updatedEntry.nbErrors = totalQuestions;

      const updatedModalSeries = [...modalSeries];
      updatedModalSeries[existingEntryIndex] = updatedEntry;

      if (goodAnswersForAd > 0) {
        return {
          shouldShow: true,
          goodAnswersForAd,
          updatedModalSeries,
        };
      }

      return { shouldShow: false, updatedModalSeries };
    }

    // Not at interval, just update counter
    const updatedModalSeries = [...modalSeries];
    updatedModalSeries[existingEntryIndex] = updatedEntry;

    return { shouldShow: false, updatedModalSeries };
  }
}
